import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Plus, Clock, Pill, Calendar, Trash2, Edit, Check, X, TrendingUp, AlertCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { API } from '../App';
import { toast, Toaster } from 'sonner';

const MedicationReminder = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [reminders, setReminders] = useState([]);
  const [activeReminders, setActiveReminders] = useState([]);
  const [adherenceData, setAdherenceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [activeTab, setActiveTab] = useState('active');

  // Form state
  const [formData, setFormData] = useState({
    medication_name: '',
    dosage: '',
    times: [''],
    frequency: 'daily',
    days: [],
    instructions: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    reminder_enabled: true,
    notes: ''
  });

  useEffect(() => {
    fetchReminders();
    fetchActiveReminders();
    fetchAdherence();
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    // Check for reminders every minute
    const interval = setInterval(() => {
      checkReminders();
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, []);

  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API}/medications/reminders`);
      setReminders(response.data.reminders || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reminders:', error);
      toast.error('Failed to load medication reminders');
      setLoading(false);
    }
  };

  const fetchActiveReminders = async () => {
    try {
      const response = await axios.get(`${API}/medications/reminders/active`);
      setActiveReminders(response.data.reminders || []);
    } catch (error) {
      console.error('Error fetching active reminders:', error);
    }
  };

  const fetchAdherence = async () => {
    try {
      const response = await axios.get(`${API}/medications/adherence?days=30`);
      setAdherenceData(response.data.adherence);
    } catch (error) {
      console.error('Error fetching adherence:', error);
    }
  };

  const checkReminders = () => {
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    activeReminders.forEach(reminder => {
      if (reminder.times && reminder.times.includes(currentTime)) {
        showNotification(reminder);
      }
    });
  };

  const showNotification = (reminder) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('Medication Reminder', {
        body: `Time to take ${reminder.medication_name} - ${reminder.dosage}`,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-192x192.png',
        tag: reminder.id,
        requireInteraction: true
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
    
    // Also show toast notification
    toast.info(`Time to take ${reminder.medication_name}`, {
      description: `${reminder.dosage}${reminder.instructions ? ' - ' + reminder.instructions : ''}`,
      duration: 10000,
      action: {
        label: 'Mark as Taken',
        onClick: () => logMedication(reminder.id, 'taken')
      }
    });
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    
    try {
      // Filter out empty times
      const validTimes = formData.times.filter(t => t.trim() !== '');
      
      if (validTimes.length === 0) {
        toast.error('Please add at least one reminder time');
        return;
      }

      const payload = {
        ...formData,
        times: validTimes,
        end_date: formData.end_date || null
      };

      await axios.post(`${API}/medications/reminders`, payload);
      
      toast.success('Medication reminder created successfully');
      setShowAddDialog(false);
      resetForm();
      fetchReminders();
      fetchActiveReminders();
    } catch (error) {
      console.error('Error creating reminder:', error);
      toast.error('Failed to create medication reminder');
    }
  };

  const handleUpdateReminder = async (e) => {
    e.preventDefault();
    
    try {
      const validTimes = formData.times.filter(t => t.trim() !== '');
      
      if (validTimes.length === 0) {
        toast.error('Please add at least one reminder time');
        return;
      }

      const payload = {
        ...formData,
        times: validTimes,
        end_date: formData.end_date || null
      };

      await axios.put(`${API}/medications/reminders/${editingReminder.id}`, payload);
      
      toast.success('Medication reminder updated successfully');
      setShowEditDialog(false);
      setEditingReminder(null);
      resetForm();
      fetchReminders();
      fetchActiveReminders();
    } catch (error) {
      console.error('Error updating reminder:', error);
      toast.error('Failed to update medication reminder');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Are you sure you want to delete this medication reminder?')) {
      return;
    }

    try {
      await axios.delete(`${API}/medications/reminders/${reminderId}`);
      toast.success('Medication reminder deleted');
      fetchReminders();
      fetchActiveReminders();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      toast.error('Failed to delete medication reminder');
    }
  };

  const logMedication = async (reminderId, status, scheduledTime = null) => {
    try {
      const now = new Date();
      const time = scheduledTime || `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      
      await axios.post(`${API}/medications/log`, {
        reminder_id: reminderId,
        scheduled_time: time,
        status: status
      });
      
      toast.success(`Medication marked as ${status}`);
      fetchAdherence();
    } catch (error) {
      console.error('Error logging medication:', error);
      toast.error('Failed to log medication');
    }
  };

  const handleEditClick = (reminder) => {
    setEditingReminder(reminder);
    setFormData({
      medication_name: reminder.medication_name,
      dosage: reminder.dosage,
      times: reminder.times || [''],
      frequency: reminder.frequency || 'daily',
      days: reminder.days || [],
      instructions: reminder.instructions || '',
      start_date: reminder.start_date,
      end_date: reminder.end_date || '',
      reminder_enabled: reminder.reminder_enabled,
      notes: reminder.notes || ''
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      medication_name: '',
      dosage: '',
      times: [''],
      frequency: 'daily',
      days: [],
      instructions: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      reminder_enabled: true,
      notes: ''
    });
  };

  const addTimeSlot = () => {
    setFormData({ ...formData, times: [...formData.times, ''] });
  };

  const removeTimeSlot = (index) => {
    const newTimes = formData.times.filter((_, i) => i !== index);
    setFormData({ ...formData, times: newTimes });
  };

  const updateTimeSlot = (index, value) => {
    const newTimes = [...formData.times];
    newTimes[index] = value;
    setFormData({ ...formData, times: newTimes });
  };

  const toggleDay = (day) => {
    const days = formData.days.includes(day)
      ? formData.days.filter(d => d !== day)
      : [...formData.days, day];
    setFormData({ ...formData, days });
  };

  const weekDays = [
    { key: 'monday', label: t('medications.monday') },
    { key: 'tuesday', label: t('medications.tuesday') },
    { key: 'wednesday', label: t('medications.wednesday') },
    { key: 'thursday', label: t('medications.thursday') },
    { key: 'friday', label: t('medications.friday') },
    { key: 'saturday', label: t('medications.saturday') },
    { key: 'sunday', label: t('medications.sunday') }
  ];

  const renderReminderForm = (isEdit = false) => (
    <form onSubmit={isEdit ? handleUpdateReminder : handleAddReminder} className="space-y-4">
      <div>
        <Label htmlFor="medication_name">{t('medications.medicationName')} *</Label>
        <Input
          id="medication_name"
          value={formData.medication_name}
          onChange={(e) => setFormData({ ...formData, medication_name: e.target.value })}
          placeholder={t('medications.medicationNamePlaceholder')}
          required
        />
      </div>

      <div>
        <Label htmlFor="dosage">{t('medications.dosage')} *</Label>
        <Input
          id="dosage"
          value={formData.dosage}
          onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
          placeholder={t('medications.dosagePlaceholder')}
          required
        />
      </div>

      <div>
        <Label>{t('medications.reminderTimes')} *</Label>
        {formData.times.map((time, index) => (
          <div key={`item-${index}`} className="flex gap-2 mb-2">
            <Input
              type="time"
              value={time}
              onChange={(e) => updateTimeSlot(index, e.target.value)}
              required
            />
            {formData.times.length > 1 && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => removeTimeSlot(index)}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
        <Button type="button" variant="outline" onClick={addTimeSlot} className="w-full mt-2">
          <Plus className="w-4 h-4 mr-2" /> {t('medications.addAnotherTime')}
        </Button>
      </div>

      <div>
        <Label htmlFor="frequency">{t('medications.frequency')}</Label>
        <select
          id="frequency"
          value={formData.frequency}
          onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
          className="w-full px-3 py-2 border rounded-md"
        >
          <option value="daily">{t('medications.daily')}</option>
          <option value="weekly">{t('medications.specificDays')}</option>
          <option value="as_needed">{t('medications.asNeeded')}</option>
        </select>
      </div>

      {formData.frequency === 'weekly' && (
        <div>
          <Label>{t('medications.selectDays')}</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {weekDays.map(day => (
              <Button
                key={day.key}
                type="button"
                variant={formData.days.includes(day.key) ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleDay(day.key)}
              >
                {day.label.substring(0, 3)}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="instructions">{t('medications.instructions')}</Label>
        <Input
          id="instructions"
          value={formData.instructions}
          onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
          placeholder="e.g., Take with food, Before breakfast"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="start_date">{t('medications.startDate')} *</Label>
          <Input
            id="start_date"
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="end_date">{t('medications.endDateOptional')}</Label>
          <Input
            id="end_date"
            type="date"
            value={formData.end_date}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="notes">{t('forms.notes')}</Label>
        <textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder={t('medications.additionalNotes')}
          className="w-full px-3 py-2 border rounded-md"
          rows="3"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="reminder_enabled"
          checked={formData.reminder_enabled}
          onChange={(e) => setFormData({ ...formData, reminder_enabled: e.target.checked })}
          className="w-4 h-4"
        />
        <Label htmlFor="reminder_enabled" className="cursor-pointer">{t('medications.enableReminders')}</Label>
      </div>

      <div className="flex gap-2 pt-4">
        <Button type="submit" className="flex-1">
          {isEdit ? t('medications.updateReminder') : t('medications.createReminder')}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            isEdit ? setShowEditDialog(false) : setShowAddDialog(false);
            resetForm();
            setEditingReminder(null);
          }}
        >
          {t('buttons.cancel')}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      <Toaster position="top-right" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                <Pill className="w-10 h-10 text-teal-600" />
                {t('medications.medicationReminder')}
              </h1>
              <p className="text-gray-600 mt-2">{t('medications.neverMiss')}</p>
            </div>
            <Button onClick={() => setShowAddDialog(true)} size="lg">
              <Plus className="w-5 h-5 mr-2" />
              {t('medications.addMedication')}
            </Button>
          </div>
        </div>

        {/* Adherence Stats */}
        {adherenceData && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-6 bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('medications.adherenceRate')}</p>
                  <p className="text-3xl font-bold text-teal-600">{adherenceData.adherence_percentage}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-teal-600" />
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('medications.taken')}</p>
                  <p className="text-3xl font-bold text-green-600">{adherenceData.taken_doses}</p>
                </div>
                <Check className="w-8 h-8 text-green-600" />
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('medications.missed')}</p>
                  <p className="text-3xl font-bold text-red-600">{adherenceData.missed_doses}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('medications.totalDoses')}</p>
                  <p className="text-3xl font-bold text-blue-600">{adherenceData.total_doses}</p>
                </div>
                <Pill className="w-8 h-8 text-blue-600" />
              </div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="active">
              <Bell className="w-4 h-4 mr-2" />
              {t('medications.activeRemindersToday')} ({activeReminders.length})
            </TabsTrigger>
            <TabsTrigger value="all">
              <Calendar className="w-4 h-4 mr-2" />
              {t('medications.allMedications')} ({reminders.length})
            </TabsTrigger>
          </TabsList>

          {/* Today's Reminders */}
          <TabsContent value="active">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">{t('common.loading')}</p>
              </div>
            ) : activeReminders.length === 0 ? (
              <Card className="p-12 text-center">
                <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('medications.noActiveReminders')}</h3>
                <p className="text-gray-600 mb-6">You don't have any medications scheduled for today</p>
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('medications.addYourFirstMedication')}
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activeReminders.map(reminder => (
                  <Card key={reminder.id} className="p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-800 mb-1">{reminder.medication_name}</h3>
                        <p className="text-teal-600 font-medium">{reminder.dosage}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="icon" onClick={() => handleEditClick(reminder)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="icon" onClick={() => handleDeleteReminder(reminder.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>

                    {reminder.instructions && (
                      <p className="text-sm text-gray-600 mb-4 italic">{reminder.instructions}</p>
                    )}

                    <div className="space-y-3">
                      {reminder.times && reminder.times.map((time, idx) => (
                        <div key={`item-${idx}`} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-600" />
                            <span className="font-medium">{time}</span>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => logMedication(reminder.id, 'taken', time)}>
                              <Check className="w-4 h-4 mr-1" />
                              Taken
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => logMedication(reminder.id, 'skipped', time)}>
                              <X className="w-4 h-4 mr-1" />
                              Skip
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {reminder.notes && (
                      <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-sm text-gray-700">{reminder.notes}</p>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* All Medications */}
          <TabsContent value="all">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">{t('common.loading')}</p>
              </div>
            ) : reminders.length === 0 ? (
              <Card className="p-12 text-center">
                <Pill className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('medications.noMedicationsYet')}</h3>
                <p className="text-gray-600 mb-6">{t('medications.startByAdding')}</p>
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('medications.addMedication')}
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {reminders.map(reminder => (
                  <Card key={reminder.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-gray-800">{reminder.medication_name}</h3>
                          {reminder.reminder_enabled ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">{t('status.active')}</span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">{t('status.inactive')}</span>
                          )}
                        </div>
                        <p className="text-teal-600 font-medium mb-2">{reminder.dosage}</p>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{reminder.times ? reminder.times.join(', ') : 'No times set'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span className="capitalize">{reminder.frequency}</span>
                          </div>
                        </div>

                        {reminder.instructions && (
                          <p className="text-sm text-gray-600 italic mb-2">{reminder.instructions}</p>
                        )}

                        {reminder.frequency === 'weekly' && reminder.days && (
                          <div className="flex gap-2 mb-3">
                            {reminder.days.map(day => (
                              <span key={day} className="px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded">
                                {day}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="text-xs text-gray-500">
                          Start: {reminder.start_date}
                          {reminder.end_date && ` • End: ${reminder.end_date}`}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button variant="outline" size="icon" onClick={() => handleEditClick(reminder)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="icon" onClick={() => handleDeleteReminder(reminder.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Reminder Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('medications.addNewMedicationReminder')}</DialogTitle>
          </DialogHeader>
          {renderReminderForm(false)}
        </DialogContent>
      </Dialog>

      {/* Edit Reminder Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('medications.editMedicationReminder')}</DialogTitle>
          </DialogHeader>
          {renderReminderForm(true)}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicationReminder;
